import React, { useState, useEffect, useMemo, useRef } from "react";
import { useCharacter } from "./CharacterProvider";
import CinzelSelect from "./CinzelSelect";
import "./CharacterForm.css";
import { toast } from "react-toastify";
import { fetchKnowledges, fetchTransforms } from "../../utils/api";

function useFlashAnimationOnChange(fieldName, value, setAnimatedFields) {
    useEffect(() => {
        if (value !== undefined) {
            setAnimatedFields((prev) => [...prev, fieldName]);
            const timer = setTimeout(() => {
                setAnimatedFields((prev) => prev.filter((f) => f !== fieldName));
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [value, fieldName, setAnimatedFields]);
}

/** Rank splitting helper (10-cap per profession/hobby) */
function getKnowledges(sp) {
    const CAP = 10;
    const totalProfessions = 1 + Math.floor((sp || 0) / 50);
    const totalHobbies = 1 + Math.floor((sp || 0) / 100);

    const split = (total) => {
        const full = Math.floor(total / CAP);
        const rem = total % CAP;
        return rem === 0 ? Array(full).fill(CAP) : [...Array(full).fill(CAP), rem];
    };

    return {
        professionRanks: split(totalProfessions),
        hobbyRanks: split(totalHobbies),
    };
}

const TYPE_PROF = "profession";
const TYPE_HOBBY = "hobby";

// De-emphasize empty stats: render blank instead of 0 so zero-value pools/armor
// don't compete visually with real numbers. (Bank intentionally still shows 0.)
const hideZero = (v) => (Number(v) ? v : "");

// Brighten a stat's label when it actually has a value, so populated pools/armor
// stand out from the dim, empty ones.
const labelClass = (v) =>
    `characterform-label${Number(v) ? " characterform-label--active" : ""}`;

const CharacterForm = ({ races, skillsets, availableSkills, catalogReady = false, staffPermissions }) => {
    const [animatedFields, setAnimatedFields] = useState([]);
    const { currentCharacter, setCurrentCharacter } = useCharacter();
    const buildScope = useRef(null);
    buildScope.current = { catalogReady, characterId: currentCharacter?.characterId, transformId: currentCharacter?.transformId };

    // Staff can reassign a character's transform; the list is loaded once and kept
    // alphabetical. Non-staff never see the dropdown, so we only fetch for staff.
    const [transforms, setTransforms] = useState([]);
    useEffect(() => {
        if (!staffPermissions) return;
        let mounted = true;
        (async () => {
            try {
                const data = await fetchTransforms();
                if (!mounted) return;
                const sorted = [...(data || [])].sort((a, b) =>
                    (a?.name || "").localeCompare((b?.name || ""), undefined, { sensitivity: "base", numeric: true })
                );
                setTransforms(sorted);
            } catch {
                /* leave the list empty; the field falls back to the current name */
            }
        })();
        return () => { mounted = false; };
    }, [staffPermissions]);

    // Load knowledge options from API
    const [knowledgeOptions, setKnowledgeOptions] = useState([]);
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const data = await fetchKnowledges();
                if (!mounted) return;
                setKnowledgeOptions(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Failed to load knowledges", err);
                toast.error("Failed to load knowledges.");
                setKnowledgeOptions([]);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    // Map for quick id->name lookup (used when rebuilding character.knowledges)
    const idToName = useMemo(() => {
        const m = new Map();
        for (const k of knowledgeOptions) m.set(k.knowledgeId, k.name || "");
        return m;
    }, [knowledgeOptions]);

    // Sorted list for nicer dropdowns
    const knowledgeOptionsSorted = useMemo(
        () => [...knowledgeOptions].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        [knowledgeOptions]
    );

    function getThaumaturgySkillId(availableSkills) {
        if (!availableSkills) return null;
        const found = availableSkills.find(
            (s) => (s.name || "").trim().toLowerCase() === "attunement: thaumaturgy"
        );
        return found?.skillId ?? null;
    }

    // --- Flash hooks (unchanged) ---
    useFlashAnimationOnChange("bodyPoints", currentCharacter?.bodyPoints, setAnimatedFields);
    useFlashAnimationOnChange("maxArmor", currentCharacter?.maxArmor, setAnimatedFields);
    useFlashAnimationOnChange("dexArmor", currentCharacter?.dexArmor, setAnimatedFields);
    useFlashAnimationOnChange("skillPointsSpent", currentCharacter?.skillPointsSpent, setAnimatedFields);
    useFlashAnimationOnChange("skillPointsUnspent", currentCharacter?.skillPointsUnspent, setAnimatedFields);
    useFlashAnimationOnChange("agilityPoints", currentCharacter?.agilityPoints, setAnimatedFields);
    useFlashAnimationOnChange("arcanePoints", currentCharacter?.arcanePoints, setAnimatedFields);
    useFlashAnimationOnChange("naturePoints", currentCharacter?.naturePoints, setAnimatedFields);
    useFlashAnimationOnChange("spiritPoints", currentCharacter?.spiritPoints, setAnimatedFields);
    useFlashAnimationOnChange("staminaPoints", currentCharacter?.staminaPoints, setAnimatedFields);
    useFlashAnimationOnChange("thaumaturgyPoints", currentCharacter?.thaumaturgyPoints, setAnimatedFields);
    useFlashAnimationOnChange("spiritBottleCharges", currentCharacter?.spiritBottleCharges, setAnimatedFields);

    // --- Derived knowledge slots from SP ---
    const { professionRanks, hobbyRanks } = useMemo(
        () => getKnowledges(currentCharacter?.skillPointsTotal ?? 0),
        [currentCharacter?.skillPointsTotal]
    );

    // ✅ Helper: pull ids by type in the stored order (slot order), NO sorting
    const idsFromKnowledges = (type, slotCount) => {
        const list = (currentCharacter?.knowledges || [])
            .filter((k) => (k.type || "").toLowerCase() === type);
        const ids = list.slice(0, slotCount).map((k) => (k?.knowledgeId ?? null));
        while (ids.length < slotCount) ids.push(null);
        return ids;
    };

    // Current selections per slot (ids or null)
    const professionIds = useMemo(
        () => idsFromKnowledges(TYPE_PROF, professionRanks.length),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentCharacter?.knowledges, professionRanks.length]
    );
    const hobbyIds = useMemo(
        () => idsFromKnowledges(TYPE_HOBBY, hobbyRanks.length),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentCharacter?.knowledges, hobbyRanks.length]
    );

    // --- Change handlers ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (currentCharacter) {
            setCurrentCharacter((prev) => ({ ...prev, [name]: value }));
        }
    };

    // Transform updates both the id (saved) and the display name in one go.
    const handleTransformChange = (e) => {
        const { value } = e.target;
        const id = value === "" ? null : Number(value);
        const picked = transforms.find((t) => String(t.transformId) === String(value));
        setAnimatedFields(["transformName"]);
        setCurrentCharacter((prev) => ({ ...prev, transformId: id, transformName: picked?.name || "" }));
    };

    const handleDropdownChange = (e) => {
        const { name, value } = e.target;
        const isSkillsetField = name.startsWith("skillset");
        if (isSkillsetField && !catalogReady) return;
        const startedScope = buildScope.current;
        const updatedValue = isSkillsetField ? Number(value) : value;

        setAnimatedFields([]);

        setTimeout(() => {
            if (isSkillsetField) {
                setCurrentCharacter((prev) => {
                    const scope = buildScope.current;
                    if (!scope.catalogReady || scope.characterId !== startedScope.characterId ||
                        scope.transformId !== startedScope.transformId) return prev;
                    const { skillsetPrimary, skillsetSecondary, skills } = prev;

                    const THAUM_SKILL_ID = getThaumaturgySkillId(availableSkills);
                    const THAUMATURGY_SKILLSET_ID = 6; // (change if needed)

                    const hasAttuneThaumaturgy =
                        THAUM_SKILL_ID &&
                        skills.some((skill) => Number(skill.skillId) === Number(THAUM_SKILL_ID));

                    if (name === "skillsetPrimary" && hasAttuneThaumaturgy && updatedValue !== THAUMATURGY_SKILLSET_ID) {
                        toast.error(
                            "You cannot change your Primary Skillset because you have the Attunement: Thaumaturgy skill."
                        );
                        return { ...prev };
                    }

                    const pickedThaumaturgyAsSecondary =
                        name === "skillsetSecondary" && updatedValue === THAUMATURGY_SKILLSET_ID;
                    const swapWouldMakeThaumaturgySecondary =
                        name === "skillsetPrimary" &&
                        updatedValue !== THAUMATURGY_SKILLSET_ID &&
                        updatedValue === skillsetSecondary &&
                        skillsetPrimary === THAUMATURGY_SKILLSET_ID;
                    if (pickedThaumaturgyAsSecondary || swapWouldMakeThaumaturgySecondary) {
                        toast.error("Thaumaturgy is not allowed to be a Secondary Skillset.");
                        return { ...prev };
                    }

                    if (name === "skillsetPrimary" && updatedValue === skillsetSecondary) {
                        setAnimatedFields(["skillsetPrimary", "skillsetSecondary"]);
                        return {
                            ...prev,
                            skillsetPrimary: updatedValue,
                            skillsetSecondary: skillsetPrimary,
                        };
                    }
                    if (name === "skillsetSecondary" && updatedValue === skillsetPrimary) {
                        setAnimatedFields(["skillsetPrimary", "skillsetSecondary"]);
                        return {
                            ...prev,
                            skillsetSecondary: updatedValue,
                            skillsetPrimary: skillsetSecondary,
                        };
                    }

                    setAnimatedFields([name]);
                    return { ...prev, [name]: updatedValue };
                });
            } else {
                setAnimatedFields([name]);
                setCurrentCharacter((prev) => ({
                    ...prev,
                    [name]: updatedValue,
                }));
            }
        }, 50);
    };

    // Rebuild character.knowledges payload from slot arrays (ids) + ranks
    const buildKnowledgePayload = (profIds, hobbyIds) => {
        const out = [];

        for (let i = 0; i < profIds.length; i++) {
            const id = profIds[i];
            if (id != null) {
                out.push({
                    knowledgeId: id,
                    name: idToName.get(id) || "",
                    type: TYPE_PROF,
                    ranks: professionRanks[i],
                });
            }
        }
        for (let i = 0; i < hobbyIds.length; i++) {
            const id = hobbyIds[i];
            if (id != null) {
                out.push({
                    knowledgeId: id,
                    name: idToName.get(id) || "",
                    type: TYPE_HOBBY,
                    ranks: hobbyRanks[i],
                });
            }
        }
        return out;
    };

    // Smart select with swap (within and across types), storing ids
    const handleKnowledgeSelect = (kind, index, rawValue) => {
        const newId = rawValue === "" ? null : Number(rawValue);

        setCurrentCharacter((prev) => {
            const curProfIds = idsFromKnowledges(TYPE_PROF, professionRanks.length);
            const curHobbyIds = idsFromKnowledges(TYPE_HOBBY, hobbyRanks.length);

            const A = kind === "professions" ? TYPE_PROF : TYPE_HOBBY;
            const B = A === TYPE_PROF ? TYPE_HOBBY : TYPE_PROF;

            const arrA = A === TYPE_PROF ? [...curProfIds] : [...curHobbyIds];
            const arrB = A === TYPE_PROF ? [...curHobbyIds] : [...curProfIds];

            const currentValue = arrA[index] ?? null;

            if (newId == null) {
                if (currentValue == null) return prev;
                arrA[index] = null;
            } else if (newId === currentValue) {
                return prev;
            } else {
                // Find duplicate in same kind
                let dupKind = A;
                let dupIndex = arrA.findIndex((v, i) => i !== index && v === newId);

                // If not found, look in other kind
                if (dupIndex === -1) {
                    dupKind = B;
                    dupIndex = arrB.findIndex((v) => v === newId);
                }

                if (dupIndex !== -1) {
                    // SWAP
                    if (dupKind === A) {
                        arrA[dupIndex] = currentValue;
                        arrA[index] = newId;
                    } else {
                        arrA[index] = newId;
                        arrB[dupIndex] = currentValue;
                    }
                } else {
                    // Simple set
                    arrA[index] = newId;
                }
            }

            const nextProfIds = A === TYPE_PROF ? arrA : arrB;
            const nextHobbyIds = A === TYPE_HOBBY ? arrA : arrB;

            const rebuilt = buildKnowledgePayload(nextProfIds, nextHobbyIds);

            return {
                ...prev,
                knowledges: rebuilt,
            };
        });
    };

    // === JSX ===
    return (
        <div className="characterform-form">
            <div className="characterform-desktop-top">
                {/* Row 1: Character | Player | Race | SubRace */}
                <div className="characterform-row">
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="name">
                            Character
                        </label>
                        <input
                            type="text"
                            name="name"
                            id="name"
                            value={currentCharacter?.name || ""}
                            onChange={handleChange}
                            className="characterform-control underline-input"
                            maxLength={50}
                        />
                    </div>
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="playerName">
                            Player
                        </label>
                        <input
                            type="text"
                            name="playerName"
                            id="playerName"
                            value={currentCharacter?.playerName || ""}
                            onChange={handleChange}
                            className="characterform-control underline-input"
                            maxLength={50}
                        />
                    </div>
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="raceId">
                            Race
                        </label>
                        <CinzelSelect
                            name="raceId"
                            id="raceId"
                            value={currentCharacter?.raceId || ""}
                            onChange={handleDropdownChange}
                            className="characterform-control"
                        >
                            {races.map((race) => (
                                <option key={race.raceId} value={race.raceId}>
                                    {race.name}
                                </option>
                            ))}
                        </CinzelSelect>
                    </div>
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="subRace">
                            Sub Race
                        </label>
                        <input
                            type="text"
                            name="subRace"
                            id="subRace"
                            value={currentCharacter?.subRace || ""}
                            onChange={handleChange}
                            className="characterform-control underline-input"
                            maxLength={50}
                        />
                    </div>
                </div>
                {/* Row 2: Primary Skillset | Secondary Skillset | Transform */}
                <div className="characterform-row">
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="skillsetPrimary">
                            Primary Skillset
                        </label>
                        <CinzelSelect
                            name="skillsetPrimary"
                            disabled={!catalogReady}
                            id="skillsetPrimary"
                            value={currentCharacter?.skillsetPrimary || ""}
                            onChange={handleDropdownChange}
                            className="characterform-control"
                        >
                            {skillsets
                                .filter((s) => s.canBeChosen)
                                .map((option) => (
                                    <option key={option.skillsetId} value={option.skillsetId}>
                                        {option.name}
                                    </option>
                                ))}
                        </CinzelSelect>
                    </div>
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="skillsetSecondary">
                            Secondary Skillset
                        </label>
                        <CinzelSelect
                            name="skillsetSecondary"
                            disabled={!catalogReady}
                            id="skillsetSecondary"
                            value={currentCharacter?.skillsetSecondary || ""}
                            onChange={handleDropdownChange}
                            className="characterform-control"
                        >
                            {skillsets
                                .filter((s) => s.canBeChosen)
                                .map((option) => (
                                    <option key={option.skillsetId} value={option.skillsetId}>
                                        {option.name}
                                    </option>
                                ))}
                        </CinzelSelect>
                    </div>
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="transformId">
                            Transform
                        </label>
                        {staffPermissions ? (
                            <CinzelSelect
                                name="transformId"
                                id="transformId"
                                value={currentCharacter?.transformId ?? ""}
                                onChange={handleTransformChange}
                                className="characterform-control"
                            >
                                <option value="">— None —</option>
                                {transforms.map((t) => (
                                    <option key={t.transformId} value={t.transformId}>
                                        {t.name}
                                    </option>
                                ))}
                            </CinzelSelect>
                        ) : (
                            <div className="characterform-value">
                                {currentCharacter?.transformName || ""}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="characterform-mobile-toprows">
                {/* Mobile Row 1: Character | Player */}
                <div className="characterform-mrow">
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="name">
                            Character
                        </label>
                        <input
                            type="text"
                            name="name"
                            id="name"
                            value={currentCharacter?.name || ""}
                            onChange={handleChange}
                            className="characterform-control underline-input"
                            maxLength={50}
                        />
                    </div>
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="playerName">
                            Player
                        </label>
                        <input
                            type="text"
                            name="playerName"
                            id="playerName"
                            value={currentCharacter?.playerName || ""}
                            onChange={handleChange}
                            className="characterform-control underline-input"
                            maxLength={50}
                        />
                    </div>
                </div>
                {/* Mobile Row 2: Race | SubRace | Transform */}
                <div className="characterform-mrow">
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="raceId">
                            Race
                        </label>
                        <CinzelSelect
                            name="raceId"
                            id="raceId"
                            value={currentCharacter?.raceId || ""}
                            onChange={handleDropdownChange}
                            className="characterform-control"
                        >
                            {races.map((race) => (
                                <option key={race.raceId} value={race.raceId}>
                                    {race.name}
                                </option>
                            ))}
                        </CinzelSelect>
                    </div>
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="subRace">
                            Sub Race
                        </label>
                        <input
                            type="text"
                            name="subRace"
                            id="subRace"
                            value={currentCharacter?.subRace || ""}
                            onChange={handleChange}
                            className="characterform-control underline-input"
                            maxLength={50}
                        />
                    </div>
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="transformId">
                            Transform
                        </label>
                        {staffPermissions ? (
                            <CinzelSelect
                                name="transformId"
                                id="transformId"
                                value={currentCharacter?.transformId ?? ""}
                                onChange={handleTransformChange}
                                className="characterform-control"
                            >
                                <option value="">— None —</option>
                                {transforms.map((t) => (
                                    <option key={t.transformId} value={t.transformId}>
                                        {t.name}
                                    </option>
                                ))}
                            </CinzelSelect>
                        ) : (
                            <div className="characterform-value">
                                {currentCharacter?.transformName || ""}
                            </div>
                        )}
                    </div>
                </div>
                {/* Mobile Row 3: Primary Skillset | Secondary Skillset */}
                <div className="characterform-mrow">
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="skillsetPrimary">
                            Primary Skillset
                        </label>
                        <CinzelSelect
                            name="skillsetPrimary"
                            disabled={!catalogReady}
                            id="skillsetPrimary"
                            value={currentCharacter?.skillsetPrimary || ""}
                            onChange={handleDropdownChange}
                            className="characterform-control"
                        >
                            {skillsets
                                .filter((s) => s.canBeChosen)
                                .map((option) => (
                                    <option key={option.skillsetId} value={option.skillsetId}>
                                        {option.name}
                                    </option>
                                ))}
                        </CinzelSelect>
                    </div>
                    <div className="characterform-group">
                        <label className="characterform-label" htmlFor="skillsetSecondary">
                            Secondary Skillset
                        </label>
                        <CinzelSelect
                            name="skillsetSecondary"
                            disabled={!catalogReady}
                            id="skillsetSecondary"
                            value={currentCharacter?.skillsetSecondary || ""}
                            onChange={handleDropdownChange}
                            className="characterform-control"
                        >
                            {skillsets
                                .filter((s) => s.canBeChosen)
                                .map((option) => (
                                    <option key={option.skillsetId} value={option.skillsetId}>
                                        {option.name}
                                    </option>
                                ))}
                        </CinzelSelect>
                    </div>
                </div>
            </div>

            {/* Skill Points Fields */}
            <div className="characterform-row">
                <div className="characterform-group">
                    <label className="characterform-label">Skill Points</label>
                    <div className="characterform-value">{currentCharacter?.skillPointsTotal ?? ""}</div>
                </div>
                <div className="characterform-group">
                    <label className="characterform-label">SP Debt</label>
                    <div className="characterform-value">{hideZero(currentCharacter?.skillPointsDebt)}</div>
                </div>
                <div className="characterform-group">
                    <label className="characterform-label">Effective SP</label>
                    <div className="characterform-value">{currentCharacter?.skillPointsEffective ?? ""}</div>
                </div>
                <div className="characterform-group">
                    <label className="characterform-label">SP Spent</label>
                    <div
                        className={`characterform-value${
                            animatedFields.includes("skillPointsSpent") ? " flash-animation" : ""
                        }`}
                    >
                        {currentCharacter?.skillPointsSpent ?? ""}
                    </div>
                </div>
                <div className="characterform-group">
                    <label className="characterform-label">Unspent SP</label>
                    <div
                        className={`characterform-value${
                            animatedFields.includes("skillPointsUnspent") ? " flash-animation" : ""
                        }`}
                    >
                        {currentCharacter?.skillPointsUnspent ?? ""}
                    </div>
                </div>
                <div className="characterform-group">
                    <label className="characterform-label">Bank</label>
                    <div className="characterform-value">{currentCharacter?.bankBalance ?? 0}</div>
                </div>
                <div className="characterform-group">
                    <label className="characterform-label">Bottle Charges</label>
                    <div
                        className={`characterform-value${
                            animatedFields.includes("spiritBottleCharges") ? " flash-animation" : ""
                        }`}
                    >
                        {Number(currentCharacter?.spiritBottleCharges) ? currentCharacter.spiritBottleCharges : "-"}
                    </div>
                </div>
            </div>

            {/* Health Info */}
            <div className="characterform-row">
                <div className="characterform-group">
                    <label className="characterform-label">Body</label>
                    <div
                        className={`characterform-value${
                            animatedFields.includes("bodyPoints") ? " flash-animation" : ""
                        }`}
                    >
                        {currentCharacter?.bodyPoints ?? ""}
                    </div>
                </div>
                <div className="characterform-group">
                    <label className={labelClass(currentCharacter?.maxArmor)}>Max Armor</label>
                    <div
                        className={`characterform-value${
                            animatedFields.includes("maxArmor") ? " flash-animation" : ""
                        }`}
                    >
                        {hideZero(currentCharacter?.maxArmor)}
                    </div>
                </div>
                <div className="characterform-group">
                    <label className={labelClass(currentCharacter?.dexArmor)}>Dex Armor</label>
                    <div
                        className={`characterform-value${
                            animatedFields.includes("dexArmor") ? " flash-animation" : ""
                        }`}
                    >
                        {hideZero(currentCharacter?.dexArmor)}
                    </div>
                </div>
            </div>

            {/* Pool Info */}
            <div className="characterform-row">
                <div className="characterform-group">
                    <label className={labelClass(currentCharacter?.agilityPoints)}>Agility</label>
                    <div
                        className={`characterform-value${
                            animatedFields.includes("agilityPoints") ? " flash-animation" : ""
                        }`}
                    >
                        {hideZero(currentCharacter?.agilityPoints)}
                    </div>
                </div>
                <div className="characterform-group">
                    <label className={labelClass(currentCharacter?.arcanePoints)}>Arcane</label>
                    <div
                        className={`characterform-value${
                            animatedFields.includes("arcanePoints") ? " flash-animation" : ""
                        }`}
                    >
                        {hideZero(currentCharacter?.arcanePoints)}
                    </div>
                </div>
                <div className="characterform-group">
                    <label className={labelClass(currentCharacter?.naturePoints)}>Nature</label>
                    <div
                        className={`characterform-value${
                            animatedFields.includes("naturePoints") ? " flash-animation" : ""
                        }`}
                    >
                        {hideZero(currentCharacter?.naturePoints)}
                    </div>
                </div>
                <div className="characterform-group">
                    <label className={labelClass(currentCharacter?.spiritPoints)}>Spirit</label>
                    <div
                        className={`characterform-value${
                            animatedFields.includes("spiritPoints") ? " flash-animation" : ""
                        }`}
                    >
                        {hideZero(currentCharacter?.spiritPoints)}
                    </div>
                </div>
                <div className="characterform-group">
                    <label className={labelClass(currentCharacter?.staminaPoints)}>Stamina</label>
                    <div
                        className={`characterform-value${
                            animatedFields.includes("staminaPoints") ? " flash-animation" : ""
                        }`}
                    >
                        {hideZero(currentCharacter?.staminaPoints)}
                    </div>
                </div>
                <div className="characterform-group">
                    <label className={labelClass(currentCharacter?.thaumaturgyPoints)}>Thaumaturgy</label>
                    <div
                        className={`characterform-value${
                            animatedFields.includes("thaumaturgyPoints") ? " flash-animation" : ""
                        }`}
                    >
                        {hideZero(currentCharacter?.thaumaturgyPoints)}
                    </div>
                </div>
            </div>

            {/* ==== Knowledges ==== */}
            <div className="characterform-row knowledges-row">
                <div className="characterform-group characterform-group--stack">
                    <div className="characterform-label">Knowledges</div>
                    <div className="knowledge-list">
                        {professionRanks.map((rank, idx) => (
                            <div className="knowledge-item" key={`prof-${idx}`}>
                                <span className="knowledge-rank">Rank {rank}</span>
                                <CinzelSelect
                                    className="characterform-control knowledge-select"
                                    value={professionIds[idx] == null ? "" : String(professionIds[idx])}
                                    onChange={(e) => handleKnowledgeSelect("professions", idx, e.target.value)}
                                    disabled={knowledgeOptionsSorted.length === 0}
                                >
                                    <option value="">-Profession-</option>
                                    {knowledgeOptionsSorted.map((k) => (
                                        <option key={k.knowledgeId} value={String(k.knowledgeId)}>
                                            {k.name}
                                        </option>
                                    ))}
                                </CinzelSelect>
                            </div>
                        ))}
                        {hobbyRanks.map((rank, idx) => (
                            <div className="knowledge-item" key={`hobby-${idx}`}>
                                <span className="knowledge-rank">Rank {rank}</span>
                                <CinzelSelect
                                    className="characterform-control knowledge-select"
                                    value={hobbyIds[idx] == null ? "" : String(hobbyIds[idx])}
                                    onChange={(e) => handleKnowledgeSelect("hobbies", idx, e.target.value)}
                                    disabled={knowledgeOptionsSorted.length === 0}
                                >
                                    <option value="">-Hobby-</option>
                                    {knowledgeOptionsSorted.map((k) => (
                                        <option key={k.knowledgeId} value={String(k.knowledgeId)}>
                                            {k.name}
                                        </option>
                                    ))}
                                </CinzelSelect>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CharacterForm;
